import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getTicket, replyToTicket, closeTicket } from '../services/supportService';
import './TicketDetail.css';

const CATEGORY_LABELS = {
  billing: 'Billing & Subscription',
  technical: 'Technical Issue',
  account: 'Account & Login',
  coaching: 'Coaching & Fitness',
  nutrition: 'Nutrition & Meal Plans',
  general: 'General'
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_on_customer: 'Awaiting Reply',
  resolved: 'Resolved',
  closed: 'Closed'
};

const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };

const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    loadTicket();
  }, [ticketId]);

  const loadTicket = async () => {
    try {
      const data = await getTicket(ticketId);
      setTicket(data.ticket);
    } catch (err) {
      toast.error('Failed to load ticket');
      navigate('/support');
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(null);
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return toast.error('Please enter a message');

    setSending(true);
    try {
      const data = await replyToTicket(ticketId, { message: replyText, screenshot });
      setTicket(data.ticket);
      setReplyText('');
      removeScreenshot();
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Close this ticket? You can still view it but won\'t be able to reply.')) return;
    setClosing(true);
    try {
      const data = await closeTicket(ticketId);
      setTicket(data.ticket);
      toast.success('Ticket closed');
    } catch (err) {
      toast.error(err.message || 'Failed to close ticket');
    } finally {
      setClosing(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="ticket-detail-page">
        <div className="support-loading">
          <div className="loading-spinner" />
          <p>Loading ticket...</p>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const isClosed = ['closed', 'resolved'].includes(ticket.status);

  return (
    <div className="ticket-detail-page">
      <div className="ticket-detail-container">
        <button className="back-link" onClick={() => navigate('/support')}>
          ← Back to Support
        </button>

        <div className="ticket-detail-header">
          <div className="ticket-detail-title-row">
            <h1>{ticket.subject}</h1>
            {!isClosed && (
              <button className="close-ticket-btn" onClick={handleClose} disabled={closing}>
                {closing ? 'Closing...' : 'Close Ticket'}
              </button>
            )}
          </div>
          <div className="ticket-detail-meta">
            <span className="ticket-number">{ticket.ticketNumber}</span>
            <span className={`ticket-status status-${ticket.status}`}>
              {STATUS_LABELS[ticket.status]}
            </span>
            <span className={`ticket-priority priority-${ticket.priority}`}>
              {PRIORITY_LABELS[ticket.priority]}
            </span>
            <span className={`ticket-category cat-${ticket.category}`}>
              {CATEGORY_LABELS[ticket.category]}
            </span>
            <span className="ticket-date">Created {formatDate(ticket.createdAt)}</span>
          </div>
        </div>

        <div className="ticket-messages">
          {ticket.messages.map((msg, i) => (
            <div key={msg._id || i} className={`ticket-message role-${msg.senderRole}`}>
              <div className="message-header">
                <span className="message-sender">{msg.senderName}</span>
                <span className={`message-role-badge role-badge-${msg.senderRole}`}>
                  {msg.senderRole === 'admin' ? 'Support' : msg.senderRole === 'system' ? 'System' : 'You'}
                </span>
                <span className="message-time">{formatDate(msg.createdAt)}</span>
              </div>
              <div className="message-body">{msg.body}</div>
              {msg.attachments && msg.attachments.length > 0 && (
                <div className="message-attachments">
                  {msg.attachments.map((att, j) => (
                    <a key={j} href={att.url} target="_blank" rel="noopener noreferrer" className="message-attachment">
                      <img src={att.url} alt={att.filename} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {!isClosed ? (
          <form className="ticket-reply-form" onSubmit={handleReply}>
            <h3>Reply</h3>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              rows={4}
              maxLength={5000}
              required
            />
            <div className="reply-actions">
              <div className="reply-left">
                {screenshotPreview ? (
                  <div className="reply-screenshot-preview">
                    <img src={screenshotPreview} alt="Preview" />
                    <button type="button" onClick={removeScreenshot}>✕</button>
                  </div>
                ) : (
                  <label className="reply-attach-btn">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleScreenshotChange}
                      hidden
                    />
                    📷 Attach Screenshot
                  </label>
                )}
              </div>
              <button type="submit" className="reply-send-btn" disabled={sending}>
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </form>
        ) : (
          <div className="ticket-closed-notice">
            This ticket is {ticket.status}. To reopen, please create a new ticket referencing {ticket.ticketNumber}.
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDetail;
